import React, {useMemo, useCallback, useState} from "react";
import {
    TableSection,
    StatusBadge,
    StatusCell,
    DescriptionText
} from "../styles/PaymentsPage.styles";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import moment from "moment";
import {Button, Popconfirm, Space, Tooltip} from "antd";
import {CloseOutlined, FileAddOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {ExpenseVoucherModal} from "./ExpenseVoucherModal";
import {LastTriedInvoicesTable} from "./lastTriedInvoices/LastTriedInvoicesTable";

export const PaymentsTable = ({
    dataSource = [],
    loading = false,
    refetch,
    showCancelButton = true
}) => {
    const stores = useStore();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const handleCancel = useCallback(
        async record => {
            await stores.courierWithdrawRequestStore.cancelPaymentRequest({id: record._id});
            refetch?.();
        },
        [refetch, stores.courierWithdrawRequestStore]
    );

    const columns = useMemo(
        () => [
            {
                title: "Tarih",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 110,
                render: createdAt => moment(createdAt).format("DD.MM.YYYY HH:mm:ss"),
                onCell: () => ({"data-label": "Tarih"})
            },
            {
                title: "Kurye İsmi",
                dataIndex: "courier",
                key: "courier",
                render: courier => courier?.fullname,
                onCell: () => ({"data-label": "Kurye İsmi"})
            },
            {
                title: "Kurye TC",
                dataIndex: "courier",
                key: "courier",
                width: 130,
                render: courier => courier?.tckn,
                onCell: () => ({"data-label": "Kurye TC"})
            },
            {
                title: "Cüzdan Bakiye",
                dataIndex: "walletBalance",
                key: "walletBalance",
                render: walletBalance => (walletBalance != null ? `${walletBalance} ₺` : "-"),
                onCell: () => ({"data-label": "Cüzdan Bakiye"})
            },
            {
                title: "Talep Edilen Tutar",
                dataIndex: "amount",
                key: "amount",
                render: val => (val != null ? `${val} ₺` : "-"),
                onCell: () => ({"data-label": "Talep Edilen Tutar"})
            },
            {
                title: "Fatura Tutarı",
                dataIndex: "invoiceAmount",
                key: "invoiceAmount",
                render: invoiceAmount => (invoiceAmount ? `${invoiceAmount} ₺` : "-"),
                onCell: () => ({"data-label": "Fatura Tutarı"})
            },
            {
                title: "Ödenecek Tutar",
                dataIndex: "payableAmount",
                key: "payableAmount",
                render: payableAmount => (payableAmount ? `${payableAmount} ₺` : "-"),
                onCell: () => ({"data-label": "Ödenecek Tutar"})
            },
            {
                title: "Fatura Numarası",
                dataIndex: "invoiceNumber",
                key: "invoiceNumber",
                render: invoiceNumber => invoiceNumber || "-",
                onCell: () => ({"data-label": "Fatura Numarası"})
            },
            {
                title: "Bekleyen Fatura Sayısı",
                dataIndex: "netsisPendingInvoiceCount",
                key: "netsisPendingInvoiceCount",
                render: val => val ?? "-",
                onCell: () => ({"data-label": "Bekleyen Fatura Sayısı"})
            },
            {
                title: "Deneme Sayısı",
                dataIndex: "invoiceMatchAttemptCount",
                key: "invoiceMatchAttemptCount",
                render: val => val ?? "-",
                onCell: () => ({"data-label": "Kaç Kez Denendi"})
            },
            {
                title: "Son Deneme Tarihi",
                dataIndex: "invoiceMatchLastAttemptAt",
                key: "invoiceMatchLastAttemptAt",
                width: 110,
                render: val => (val ? moment(val).format("DD.MM.YYYY HH:mm:ss") : "-"),
                onCell: () => ({"data-label": "Son Deneme Tarihi"})
            },
            {
                title: "Ödeme Durumu",
                dataIndex: "humanReadableStatus",
                key: "humanReadableStatus",
                width: 220,
                render: (humanReadableStatus, record) => {
                    return (
                        <StatusCell>
                            <StatusBadge
                                className={`status-${humanReadableStatus}`}
                                status={record.status}
                            >
                                {humanReadableStatus}
                            </StatusBadge>
                            {record.status === "failed" && record.errorMessage && (
                                <Tooltip title={record.errorMessage}>
                                    <InfoCircleOutlined
                                        style={{
                                            color: "#8c8c8c",
                                            fontSize: 14,
                                            cursor: "pointer"
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {record.canAddExpenseVoucher && (
                                <Tooltip title="İlgili talep için gider pusulası yüklenmesi gereklidir.">
                                    <InfoCircleOutlined
                                        style={{
                                            color: "#1890ff",
                                            fontSize: 14,
                                            cursor: "pointer"
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </StatusCell>
                    );
                },
                onCell: () => ({"data-label": "Ödeme Durumu"})
            },
            {
                title: "Açıklama",
                dataIndex: "description",
                key: "description",
                width: 220,
                render: val =>
                    val ? (
                        <Tooltip title={val}>
                            <DescriptionText>{val}</DescriptionText>
                        </Tooltip>
                    ) : (
                        "-"
                    ),
                onCell: () => ({"data-label": "Açıklama"})
            },
            ...(showCancelButton
                ? [
                      {
                          title: "Aksiyon",
                          key: "action",
                          width: 120,
                          render: (_, record) => (
                              <Space>
                                  <Popconfirm
                                      title="İptal Et"
                                      onConfirm={() => handleCancel(record)}
                                  >
                                      <Tooltip title="İptal Et">
                                          <Button
                                              type="primary"
                                              danger
                                              size="small"
                                              icon={<CloseOutlined />}
                                              disabled={
                                                  record.status === "waitingForBankApprove" ||
                                                  record.status === "approved" ||
                                                  record.status === "failed" ||
                                                  record.status === "canceled"
                                              }
                                          />
                                      </Tooltip>
                                  </Popconfirm>
                                  {record.canAddExpenseVoucher && (
                                      <Tooltip title="Gider Pusulası Yükle">
                                          <Button
                                              type="primary"
                                              size="small"
                                              icon={<FileAddOutlined />}
                                              onClick={() => setSelectedRecord(record)}
                                          />
                                      </Tooltip>
                                  )}
                              </Space>
                          ),
                          onCell: () => ({"data-label": "Aksiyon"})
                      }
                  ]
                : [])
        ],
        [showCancelButton, handleCancel]
    );

    return (
        <TableSection>
            <ResponsiveDataTable
                columns={columns}
                dataSource={dataSource}
                rowKey="_id"
                loading={loading}
                expandable={{
                    expandedRowRender: record => (
                        <LastTriedInvoicesTable courierWithdrawRequestId={record._id} />
                    )
                }}
                pagination={{
                    pageSize: 10,
                    showTotal: total => `Toplam ${total} kayıt`
                }}
            />
            <ExpenseVoucherModal
                visible={!!selectedRecord}
                record={selectedRecord}
                onClose={() => setSelectedRecord(null)}
                onSuccess={refetch}
            />
        </TableSection>
    );
};
